import { Transition } from '@headlessui/react'
import { Button, icons } from '@portal/components'
import React, { useEffect, useMemo, useRef, useState } from 'react'
import AutoSizer from 'react-virtualized-auto-sizer'

import { DQLResult } from '../../types'
import { findCollectionName, validateJsonObjectContent } from '../../utils'
import { useDQLEditorContext } from '../dqlEditor/context'
import QueryResultJson from './QueryResultJson'
import QueryResultsTableHeader from './QueryResultsTableHeader'
import QueryResultTable, { DQLMutationResult } from './QueryResultTable'
import ResultsLoading from './ResultsLoading'

const { Trash } = icons

type QueryResultsTableProps = {
  title?: string
  showResults?: boolean
  allowDelete?: boolean
  action?: 'export' | 'import'
  canSelectRows?: boolean
  sideBarOpen?: boolean
}

/**
 * A wrapper for the QueryResultTable component that handles the initial transforming of
 * the result data into a format that the QueryResultTable component can use
 */
export default function QueryResultTableWrapper({
  title = 'Results',
  showResults = true,
  allowDelete = true,
  action = 'export',
  canSelectRows = true,
  sideBarOpen = false,
}: QueryResultsTableProps) {
  const { activeView, result, isLoading, query, setQuery, setRefetchInterval } =
    useDQLEditorContext()

  const [selectedCollection, setSelectedCollection] = useState<string | null>(
    null,
  )
  const [selectedRows, setSelectedRows] = useState<(string | number)[]>([])
  const [selectRowsTypeError, setSelectRowsTypeError] = useState<string | null>(
    null,
  )
  const [selectedView, setSelectedView] = useState<'json' | 'table'>('json')

  // Reset row selection and apply selection to selected collection on new query
  useEffect(() => {
    setSelectedRows([])
    setSelectRowsTypeError(null)
    setSelectedCollection(query ? findCollectionName(query) : null)
  }, [query])

  // Reset the selectedRow when the selectedView changes
  useEffect(() => {
    setSelectedRows([])
  }, [selectedView])

  // Track previous activeView to detect changes
  // Reset refetchInterval when view changes to something different
  // This is to make it a bit more flexible in case we add different
  // views in the future.
  const previousViewRef = useRef(activeView)
  useEffect(() => {
    if (activeView !== previousViewRef.current) {
      setRefetchInterval(false)
      previousViewRef.current = activeView
    }
  }, [activeView, setRefetchInterval])

  const data = useMemo<DQLResult[] | DQLMutationResult[]>(() => {
    if (!result) return []

    const { items, mutatedDocumentIds } = result

    if (items.length) return items

    if (mutatedDocumentIds?.length) {
      return mutatedDocumentIds.map((id) => ({
        'Mutated document ID': id,
      }))
    }

    return []
  }, [result])

  const shouldDisplayTable = useMemo(
    () => selectedView === 'table' || activeView === 'collections',
    [selectedView, activeView],
  )

  const handleSelectedRowsChange = (selectedRows: (string | number)[]) => {
    setSelectedRows(selectedRows)

    // Normalize types - check if string values are actually JSON objects
    const normalizedTypes = selectedRows.map((row) => {
      if (typeof row === 'string' && validateJsonObjectContent(row)) {
        const parsed = JSON.parse(row)

        if (typeof parsed === 'object' && parsed !== null) {
          return 'object'
        }
      }

      return typeof row
    })

    if (new Set(normalizedTypes).size > 1) {
      setSelectRowsTypeError(
        '_id must be of the same type (string, number, composite, etc)',
      )
    } else {
      setSelectRowsTypeError(null)
    }
  }

  const handleDelete = () => {
    if (!selectedCollection || !selectedRows.length) return

    // Handle different types of _id values when tombstoning.
    const formattedRows = selectedRows
      .map((row) => {
        if (typeof row === 'number') return row

        if (validateJsonObjectContent(row)) {
          const parsedRow = JSON.parse(row)

          if (typeof parsedRow === 'object' && parsedRow !== null) {
            return `deserialize_json('${JSON.stringify(parsedRow)}')`
          }
        }

        return `'${row}'`
      })
      .join(', ')

    // We don't need to check for backticks, as the collection name is
    // already wrapped in backticks from findCollectionName()
    setQuery(
      `TOMBSTONE FROM ${selectedCollection} WHERE _id IN (${formattedRows})`,
    )
  }

  const hasDeletions = Boolean(result?.mutatedDocumentIds?.length)
  const isAbleToSelectRows = Boolean(
    canSelectRows && selectedCollection && !hasDeletions,
  )

  if (isLoading) return <ResultsLoading />

  return (
    <div className="flex h-full flex-col">
      <QueryResultsTableHeader
        title={title}
        showResults={showResults}
        action={action}
        sideBarOpen={sideBarOpen}
        data={data}
        selectedView={selectedView}
        onSelectedViewChange={setSelectedView}
      />

      {selectedView === 'table' && allowDelete && (
        <Transition
          show={!!selectedCollection && !!selectedRows.length}
          enter="transition-height ease-in-out duration-500"
          enterFrom="h-0"
          enterTo="h-12"
          leave="transition-height ease-in-out duration-500"
          leaveFrom="h-12"
          leaveTo="h-0"
          as="div"
        >
          <div className="box-border flex h-12 w-full items-center gap-x-4 border-b border-solid border-border-normal px-4">
            <div className="flex h-full items-center gap-4">
              <Button
                variant="critical"
                onClick={handleDelete}
                aria-label={`Delete ${selectedRows.length} selected items`}
                disabled={!!selectRowsTypeError}
              >
                <Trash size={16} className="mr-2" aria-hidden="true" />
                Delete selection ({selectedRows.length})
              </Button>

              {selectRowsTypeError && (
                <div className="flex items-center gap-2 text-red-500 dark:text-red-400">
                  <span>{selectRowsTypeError}</span>
                </div>
              )}
            </div>
          </div>
        </Transition>
      )}

      <div className="min-h-0 w-full flex-1 bg-background-surface">
        {shouldDisplayTable && (
          <AutoSizer>
            {({ height, width }: { height: number; width: number }) => (
              <QueryResultTable
                height={height}
                width={width}
                data={data}
                canSelectRows={isAbleToSelectRows}
                onSelectedRowsChange={handleSelectedRowsChange}
              />
            )}
          </AutoSizer>
        )}

        {!shouldDisplayTable && <QueryResultJson data={data} />}
      </div>
    </div>
  )
}
